import { uniq } from "es-toolkit";
import { useMemo } from "react";

import type { CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";
import { useTokenOrderbook } from "@/hooks/use-token-orderbook";
import { focusToUsd } from "@/utils/currency";
import { getUserName } from "@/utils/usernames";

export const usePreLaunchOverviewLogic = ({
  tokenDetails,
  publicKey,
  focusExchangeRate,
}: {
  publicKey: string | null;
  tokenDetails?: CoinPropertiesResponse | null;
  focusExchangeRate: number | null;
}) => {
  const tokenPublicKey =
    tokenDetails?.coinProperties.Profile.PublicKeyBase58Check;

  const coinUserName = tokenDetails?.coinProperties.Profile.Username;

  const username = getUserName({
    account: {
      publicKey: tokenPublicKey,
      username: coinUserName,
    },
    customPrefix: "$",
  });

  const { tokenOrderbook, loading: loadingTokenOrderbook } = useTokenOrderbook({
    baseCurrencyPublicKeyBase58Check:
      tokenDetails?.coinProperties?.AmmConfigs?.[0]?.BaseCurrencyPkid ??
      publicKey ??
      "",
    quoteCurrencyPublicKeyBase58Check:
      tokenDetails?.coinProperties.AmmConfigs?.[0]?.QuoteCurrencyPkid ??
      process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
  });

  const { totalBidsUsd, totalBidsFocus, uniqueWallets, totalBids } =
    useMemo(() => {
      if (!tokenOrderbook?.bids) {
        return {
          totalBidsUsd: 0,
          totalBidsFocus: 0,
          uniqueWallets: [],
          totalBids: 0,
        };
      }

      const totalBidsFocus = tokenOrderbook.bids.reduce(
        (sum, bid) => sum + bid.AmountInQuoteCurrency,
        0,
      );

      const totalBidsUsd = focusToUsd(totalBidsFocus, focusExchangeRate ?? 0);
      const uniqueWallets = uniq(
        tokenOrderbook.bids.map((bid) => bid?.TransactorPublicKeyBase58Check),
      );
      const totalBids = tokenOrderbook.bids.length;

      return { totalBidsUsd, totalBidsFocus, uniqueWallets, totalBids };
    }, [tokenOrderbook, focusExchangeRate]);

  return {
    loadingTokenOrderbook,
    totalBidsUsd,
    totalBidsFocus,
    uniqueWallets,
    totalBids,
    username,
    coinUserName,
    tokenPublicKey,
    tokenOrderbook,
  };
};
